import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { useUser } from '@clerk/clerk-react';

/**
 * Component that automatically captures room thumbnails after scene renders
 * This runs invisibly in the background and stores base64 thumbnails in room metadata
 */
const ThumbnailCapture = ({ roomId, enabled = true }) => {
  const { gl } = useThree();
  const { user, isSignedIn } = useUser();

  useEffect(() => {
    if (!enabled || !roomId) return;

    // Thumbnail capture is currently disabled
    // The auto-save functionality handles room persistence without needing thumbnails

    // If we want to re-enable it in the future, uncomment the code below:
    /*
    // Function to capture thumbnail
    const captureThumbnail = async () => {
      try {
        const now = Date.now();
        // Only capture once every 30 seconds to avoid spam
        if (now - lastCaptureTime.current < 30000 && hasCapturedInitial.current) {
          return;
        }

        console.log('📸 Capturing room thumbnail...');

        // Wait a moment for scene to fully render
        await new Promise(resolve => setTimeout(resolve, 500));

        // Capture the canvas
        const canvas = gl.domElement;

        // Create a smaller thumbnail
        const tempCanvas = document.createElement('canvas');
        const targetWidth = 400;
        const targetHeight = 300;
        tempCanvas.width = targetWidth;
        tempCanvas.height = targetHeight;

        const ctx = tempCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, 0, targetWidth, targetHeight);

        // Convert to base64 with compression
        const dataUrl = tempCanvas.toDataURL('image/jpeg', 0.7);

        // Check size (base64 data URLs can be large)
        const sizeInKB = Math.round((dataUrl.length * 3) / 4 / 1024);
        console.log(`📸 Thumbnail captured: ${sizeInKB}KB`);

        // Save to room metadata via PUT to avoid overwriting other data
        const userInfo = { user, isSignedIn };

        // Use PUT endpoint to only update thumbnail
        roomApi.updateRoom(roomId, { thumbnailUrl: dataUrl }, userInfo).then(() => {
          debugLogger.success('THUMBNAIL_SAVE', `Thumbnail saved for room ${roomId}`);
          lastCaptureTime.current = now;
          hasCapturedInitial.current = true;
        }).catch(err => {
          // Log all errors with full details
          const errorDetails = {
            status: err.response?.status,
            statusText: err.response?.statusText,
            data: err.response?.data,
            message: err.message,
            url: err.config?.url,
            method: err.config?.method
          };
          debugLogger.error('THUMBNAIL_SAVE', `Failed to save thumbnail for room ${roomId}`, errorDetails);

          // Log raw response for debugging
          console.error('❌ THUMBNAIL 400 ERROR - Full Response:', {
            status: err.response?.status,
            headers: err.response?.headers,
            data: JSON.stringify(err.response?.data),
            config: {
              url: err.config?.url,
              method: err.config?.method,
              data: err.config?.data ? `${err.config.data.substring(0, 100)}...` : null
            }
          });
        });

      } catch (error) {
        console.error('Failed to capture thumbnail:', error);
      }
    };

    // Capture after a delay to ensure scene is fully loaded
    const scheduleCapture = () => {
      if (captureTimeout.current) {
        clearTimeout(captureTimeout.current);
      }

      captureTimeout.current = setTimeout(() => {
        captureThumbnail();
      }, 3000); // Wait 3 seconds after scene changes before capturing
    };

    // Schedule initial capture
    scheduleCapture();

    // Cleanup
    return () => {
      if (captureTimeout.current) {
        clearTimeout(captureTimeout.current);
      }
    };
    */
  }, [roomId, gl, user, isSignedIn, enabled]);

  return null; // This component doesn't render anything
};

export default ThumbnailCapture;
